/**
 * Module Hierarchy Map — 순수 선택·가시성·하이라이트 계획
 *
 * 다른 지도에서도 재사용:
 *   1) siteDesign 등으로 OL Feature 캐시
 *   2) resolveHierarchyPks / filterFeaturesBySelectionScope 로 표시 범위 결정
 *   3) createModuleHierarchyMapController.syncSource + applyModuleHierarchyHighlightStyles
 *
 * PK 규칙: array.id → solar_array_seq, str.id → solar_string_seq, mod.id → solar_module_seq
 * 표시값: arrayId / stringId / moduleId (array_id, string_id, module_id)
 */
import { PANEL_MAP_STYLE } from '@/features/panel-planning/utils/panelMapStyle';
import { PANEL_HIGHLIGHT_STYLE } from '@/features/panel-planning/utils/panelHighlightStyle';
import {
  MODULE_HIERARCHY_FEATURE_KEYS,
  MODULE_HIERARCHY_ROW_KIND,
  isModuleRowKind
} from './moduleHierarchyKeys';

export { MODULE_HIERARCHY_FEATURE_KEYS, MODULE_HIERARCHY_ROW_KIND };

const K = MODULE_HIERARCHY_FEATURE_KEYS;

/** 대시보드 Module focus — 초록 (panel-planning의 빨강 moduleSelected와 구분) */
const DASHBOARD_MODULE_FOCUS_STYLE = {
  stroke: '#1B5E20',
  fill: 'rgba(76, 175, 80, 0.52)',
  strokeWidth: 4,
  haloExtra: 4
};

export const DEFAULT_FOCUS_STYLES = {
  [MODULE_HIERARCHY_ROW_KIND.ARRAY]: PANEL_MAP_STYLE.arraySelected,
  [MODULE_HIERARCHY_ROW_KIND.STRING]: PANEL_MAP_STYLE.stringSelected,
  [MODULE_HIERARCHY_ROW_KIND.MODULE]: DASHBOARD_MODULE_FOCUS_STYLE,
  [MODULE_HIERARCHY_ROW_KIND.MODULE_SINGLE]: DASHBOARD_MODULE_FOCUS_STYLE
};

export const DEFAULT_CONTEXT_STYLES = {
  array: { ...PANEL_HIGHLIGHT_STYLE.array, fill: 'rgba(255,152,0,0.08)', width: 2 },
  string: { ...PANEL_HIGHLIGHT_STYLE.string, fill: 'rgba(30,136,229,0.08)', width: 2 }
};

function eq(a, b) {
  return a != null && b != null && String(a) === String(b);
}

function featureGet(f, key) {
  return typeof f?.get === 'function' ? f.get(key) : f?.[key];
}

/**
 * @typedef {'array'|'string'|'module'} HierarchyTier
 * @typedef {{ tier: HierarchyTier, arraySeq, stringSeq, moduleSeq, rowKind: string }} HierarchyPks
 */

/**
 * Drawer / 지도 클릭 payload → 계층 PK
 * @param {object|null} target
 * @returns {HierarchyPks|null}
 */
export function resolveHierarchyPks(target) {
  if (!target?.kind) return null;
  const { kind, seq } = target;

  if (kind === MODULE_HIERARCHY_ROW_KIND.ARRAY) {
    return {
      tier: 'array',
      rowKind: kind,
      arraySeq: seq,
      stringSeq: null,
      moduleSeq: null
    };
  }

  if (kind === MODULE_HIERARCHY_ROW_KIND.STRING) {
    return {
      tier: 'string',
      rowKind: kind,
      arraySeq: target.arraySeq ?? null,
      stringSeq: seq,
      moduleSeq: null
    };
  }

  if (isModuleRowKind(kind)) {
    return {
      tier: 'module',
      rowKind: kind,
      arraySeq: target.arraySeq ?? null,
      stringSeq: target.stringSeq ?? null,
      moduleSeq: seq
    };
  }

  return null;
}

/**
 * @param {object|null} target
 * @returns {string|null} e.g. "string::42:7:" — source 동기화 캐시 키
 */
export function buildSelectionScopeKey(target) {
  const pks = resolveHierarchyPks(target);
  if (!pks) return null;
  return [pks.tier, pks.arraySeq ?? '', pks.stringSeq ?? '', pks.moduleSeq ?? ''].join(':');
}

/**
 * 선택 계층에 맞는 가시 모듈 반환 (선택한 tier만 — 상위 Array로 확장하지 않음)
 * - array: 해당 array 모듈만
 * - string: 해당 string 모듈만
 * - module: 해당 모듈 1개만
 * @param {import('ol/Feature').default[]} features
 * @param {object} target
 */
export function filterFeaturesBySelectionScope(features, target) {
  const pks = resolveHierarchyPks(target);
  if (!pks || seqMissingForTier(pks)) return [];

  return (features || []).filter(f => featureMatchesPks(f, pks));
}

function seqMissingForTier(pks) {
  if (pks.tier === 'array') return pks.arraySeq == null || pks.arraySeq === '';
  if (pks.tier === 'string') return pks.stringSeq == null || pks.stringSeq === '';
  return pks.moduleSeq == null || pks.moduleSeq === '';
}

function featureMatchesPks(f, pks) {
  if (pks.tier === 'array') return eq(featureGet(f, K.arraySeq), pks.arraySeq);
  if (pks.tier === 'string') return eq(featureGet(f, K.stringSeq), pks.stringSeq);
  return eq(featureGet(f, K.moduleSeq), pks.moduleSeq);
}

function dedupeFeaturesByModuleSeq(features) {
  const seen = new Set();
  const out = [];
  for (const f of features || []) {
    const key = featureGet(f, K.moduleSeq) ?? f;
    const id = String(key);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(f);
  }
  return out;
}

/**
 * 전체 캐시 기준 focus + 부모 context (string → array siblings, module → string + array)
 * @param {import('ol/Feature').default[]} allFeatures
 * @param {object} target
 */
export function resolveHighlightWithinScope(allFeatures, target) {
  const pks = resolveHierarchyPks(target);
  const empty = { focus: [], contextString: [], contextArray: [], focusTier: pks?.tier ?? null, rowKind: target?.kind };
  if (!allFeatures?.length || !pks) return empty;

  const selection = resolveDashboardMapSelection(allFeatures, target);
  return {
    ...selection,
    rowKind: pks.rowKind
  };
}

/** @deprecated — resolveHierarchyPks(target).arraySeq 사용 */
export function resolveSelectedArraySeq(target) {
  return resolveHierarchyPks(target)?.arraySeq ?? null;
}

/** @deprecated — filterFeaturesBySelectionScope 사용 */
export function filterFeaturesByArraySeq(features, arraySeq) {
  if (arraySeq == null || arraySeq === '') return [];
  return (features || []).filter(f => eq(featureGet(f, K.arraySeq), arraySeq));
}

/**
 * 레거시: 전체 캐시에서 focus/context 분리 (가시성 필터 전 패널플래닝 스타일)
 */
export function resolveDashboardMapSelection(allFeatures, target) {
  const { kind, seq } = target || {};
  const focus = [];
  const contextString = [];
  const contextArray = [];

  if (!allFeatures?.length || seq == null || seq === '') {
    return { focus, contextString, contextArray, focusTier: kind };
  }

  if (kind === MODULE_HIERARCHY_ROW_KIND.ARRAY) {
    allFeatures.forEach(f => {
      if (eq(featureGet(f, K.arraySeq), seq)) focus.push(f);
    });
    return { focus, contextString, contextArray, focusTier: 'array' };
  }

  if (kind === MODULE_HIERARCHY_ROW_KIND.STRING) {
    const arraySeq = target.arraySeq ?? allFeatures.find(f => eq(featureGet(f, K.stringSeq), seq))?.get?.(K.arraySeq);
    allFeatures.forEach(f => {
      if (eq(featureGet(f, K.stringSeq), seq)) focus.push(f);
      else if (eq(featureGet(f, K.arraySeq), arraySeq)) contextArray.push(f);
    });
    return { focus, contextString, contextArray, focusTier: 'string' };
  }

  const moduleSeq = seq;
  const anchor = allFeatures.find(f => eq(featureGet(f, K.moduleSeq), moduleSeq));
  const stringSeq = target.stringSeq ?? featureGet(anchor, K.stringSeq);
  const arraySeq = target.arraySeq ?? featureGet(anchor, K.arraySeq);

  allFeatures.forEach(f => {
    if (eq(featureGet(f, K.moduleSeq), moduleSeq)) {
      focus.push(f);
      return;
    }
    if (eq(featureGet(f, K.stringSeq), stringSeq)) {
      contextString.push(f);
      return;
    }
    if (eq(featureGet(f, K.arraySeq), arraySeq)) {
      contextArray.push(f);
    }
  });

  return { focus, contextString, contextArray, focusTier: 'module' };
}

export function getDashboardFocusStyle(kind, styles = DEFAULT_FOCUS_STYLES) {
  return styles[kind] || styles[MODULE_HIERARCHY_ROW_KIND.MODULE];
}

export function getDashboardContextStyles(styles = DEFAULT_CONTEXT_STYLES) {
  return styles;
}

export function buildDashboardSelectionLabel(target) {
  const { kind, displayId, arrayDisplayId, stringDisplayId, seq, arraySeq, stringSeq } = target || {};
  const id = (v, s) => v ?? s ?? '?';

  if (kind === MODULE_HIERARCHY_ROW_KIND.ARRAY) {
    return `Array ${id(displayId, seq)}`;
  }
  if (kind === MODULE_HIERARCHY_ROW_KIND.STRING) {
    return `Array ${id(arrayDisplayId, arraySeq)} · String ${id(displayId, seq)}`;
  }
  return `Array ${id(arrayDisplayId, arraySeq)} · String ${id(stringDisplayId, stringSeq)} · Module ${id(displayId, seq)}`;
}

/**
 * 선택 1건에 대한 지도 반영 계획 (순수)
 * @param {import('ol/Feature').default[]} featureCache
 * @param {object} target
 */
export function planModuleHierarchyMapSelection(featureCache, target) {
  const highlight = resolveHighlightWithinScope(featureCache, target);
  const visible = dedupeFeaturesByModuleSeq([
    ...(highlight.focus || []),
    ...(highlight.contextString || []),
    ...(highlight.contextArray || [])
  ]);
  return {
    target,
    visible,
    highlight,
    label: buildDashboardSelectionLabel(target),
    scopeKey: buildSelectionScopeKey(target),
    pks: resolveHierarchyPks(target)
  };
}
